import React, { useRef } from 'react';
import { Box, TextField, Chip } from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  TipsAndUpdates as TipIcon,
  PriorityHigh as ImportantIcon,
} from '@mui/icons-material';
import { ContentBlock, CalloutVariant } from '../../../types/blocks';
import VariableInsertButton from './VariableInsertButton';

interface CalloutBlockEditorProps {
  block: ContentBlock;
  onChange: (block: ContentBlock) => void;
}

const variantConfig: { variant: CalloutVariant; label: string; icon: React.ReactElement; color: string; bg: string }[] = [
  { variant: 'info', label: 'Info', icon: <InfoIcon sx={{ fontSize: 16 }} />, color: '#3b82f6', bg: '#eff6ff' },
  { variant: 'warning', label: 'Warning', icon: <WarningIcon sx={{ fontSize: 16 }} />, color: '#f59e0b', bg: '#fffbeb' },
  { variant: 'tip', label: 'Tip', icon: <TipIcon sx={{ fontSize: 16 }} />, color: '#22c55e', bg: '#f0fdf4' },
  { variant: 'important', label: 'Important', icon: <ImportantIcon sx={{ fontSize: 16 }} />, color: '#ef4444', bg: '#fef2f2' },
];

const CalloutBlockEditor: React.FC<CalloutBlockEditorProps> = ({ block, onChange }) => {
  const currentVariant = (block.meta?.variant as CalloutVariant) || 'info';
  const config = variantConfig.find((v) => v.variant === currentVariant) || variantConfig[0];
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleInsertVariable = (token: string) => {
    const textarea = inputRef.current;
    if (!textarea) {
      onChange({ ...block, content: block.content + token });
      return;
    }
    const start = textarea.selectionStart ?? block.content.length;
    const end = textarea.selectionEnd ?? block.content.length;
    const before = block.content.slice(0, start);
    const after = block.content.slice(end);
    onChange({ ...block, content: before + token + after });
    requestAnimationFrame(() => {
      const newPos = start + token.length;
      textarea.selectionStart = newPos;
      textarea.selectionEnd = newPos;
      textarea.focus();
    });
  };

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${config.color}`,
        bgcolor: config.bg,
        borderRadius: 1,
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
        {variantConfig.map((v) => (
          <Chip
            key={v.variant}
            icon={v.icon}
            label={v.label}
            size="small"
            variant={currentVariant === v.variant ? 'filled' : 'outlined'}
            onClick={() => onChange({ ...block, meta: { ...block.meta, variant: v.variant } })}
            sx={{
              cursor: 'pointer',
              ...(currentVariant === v.variant && {
                bgcolor: v.color,
                color: '#fff',
                '& .MuiChip-icon': { color: '#fff' },
              }),
            }}
          />
        ))}
      </Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Callout title (optional)"
        value={block.meta?.title || ''}
        onChange={(e) => onChange({ ...block, meta: { ...block.meta, title: e.target.value } })}
        sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontWeight: 600, fontSize: '0.8125rem' } }}
      />
      <TextField
        fullWidth
        multiline
        minRows={2}
        placeholder="Callout body text..."
        value={block.content}
        onChange={(e) => onChange({ ...block, content: e.target.value })}
        inputRef={inputRef}
        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem' } }}
      />
      <VariableInsertButton onInsert={handleInsertVariable} />
    </Box>
  );
};

export default CalloutBlockEditor;
